import type { SmartTagRuleInput } from '@graphql/generated-base';
import type { RsvpSmartAudienceStatus } from './smartAudience.types';

export const RSVP_STATE_RESOLVER = 'rsvp-state';
const SMART_TAG_RULE_VERSION = 1;

const RSVP_STATUSES: ReadonlyArray<RsvpSmartAudienceStatus> = ['attending', 'declined', 'notResponded'];

const isRsvpStatus = (value: unknown): value is RsvpSmartAudienceStatus => typeof value === 'string' && (RSVP_STATUSES as ReadonlyArray<string>).includes(value);

export const buildRsvpStateRule = (status: RsvpSmartAudienceStatus): SmartTagRuleInput => ({
  version: SMART_TAG_RULE_VERSION,
  resolver: RSVP_STATE_RESOLVER,
  params: { status },
});

/**
 * The RSVP status a rule selects, or `null` for any rule this client doesn't know how to name — a
 * newer resolver, or a status added server-side. Callers treat `null` as "a smart audience we can't
 * describe", never as "no smart audience".
 */
export const rsvpStatusOfRule = (rule: Readonly<{ resolver: string; params: unknown }> | null | undefined): RsvpSmartAudienceStatus | null => {
  if (!rule || rule.resolver !== RSVP_STATE_RESOLVER) return null;
  const status = (rule.params as { status?: unknown } | null)?.status;
  return isRsvpStatus(status) ? status : null;
};

export const smartAudienceIdFor = (status: RsvpSmartAudienceStatus): string => `${RSVP_STATE_RESOLVER}:${status}`;

/** Whether two rules name the same audience, ignoring key order in `params`. */
export const isSameSmartRule = (a: SmartTagRuleInput | null | undefined, b: SmartTagRuleInput | null | undefined): boolean => {
  if (!a || !b) return a === b;
  const statusA = rsvpStatusOfRule(a);
  return a.resolver === b.resolver && a.version === b.version && statusA !== null && statusA === rsvpStatusOfRule(b);
};
